import { useEffect, useRef, useState } from "react";
import axios from "axios";
import api from "../../services/api";

const boundedHistory = messages => {
  const selected = [];
  let characters = 0;
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const message = messages[index];
    if (selected.length >= 12 || characters + message.content.length > 12000) break;
    selected.push({ role: message.role, content: message.content });
    characters += message.content.length;
  }
  return selected.reverse();
};

const errorMessage = error => {
  const code = String(error?.response?.data?.error || "");
  if (error?.response?.status === 429) return "O limite de testes foi atingido. Aguarde um momento e tente novamente.";
  if (/HISTORY|CONVERSATION/.test(code)) return "O histórico atingiu o limite. Inicie uma nova conversa.";
  if (error?.response?.status === 403) return "Seu usuário não possui permissão para testar agentes.";
  if (/CREDENTIAL|MODEL/.test(code)) return "Revise o modelo e a credencial nas configurações do agente.";
  return "Não foi possível executar o teste. Confira a configuração do agente e tente novamente.";
};

export default function useAgentPreview({ open, prompt, blocked, configurationKey }) {
  const requestRef = useRef(null);
  const [messages, setMessages] = useState([]);
  const [question, setQuestion] = useState("");
  const [running, setRunning] = useState(false);
  const [notice, setNotice] = useState(null);
  const key = configurationKey ?? JSON.stringify(prompt || null);

  useEffect(() => {
    setMessages([]);
    setQuestion("");
    setNotice(null);
    setRunning(false);
    return () => {
      const source = requestRef.current;
      requestRef.current = null;
      source?.cancel("preview configuration changed or closed");
    };
  }, [open, key, blocked]);

  const start = () => {
    if (!open || blocked || !prompt?.id || requestRef.current) return null;
    const source = axios.CancelToken.source();
    requestRef.current = source;
    setRunning(true);
    setNotice(null);
    return source;
  };
  const request = async (url, body, apply) => {
    const source = start();
    if (!source) return;
    try {
      const { data } = await api.post(url, body, { cancelToken: source.token });
      if (requestRef.current !== source) return;
      apply(data);
    } catch (error) {
      if (requestRef.current === source && !axios.isCancel(error)) setNotice({ severity: "error", text: errorMessage(error) });
    } finally {
      if (requestRef.current === source) { requestRef.current = null; setRunning(false); }
    }
  };
  const send = () => {
    const message = question.trim();
    if (!message) return;
    return request("/ai/preview", { promptId: prompt?.id, message, history: boundedHistory(messages), contactName: "Cliente de teste" }, data => {
      if (!data.response) {
        setNotice({ severity: "error", text: "O agente não pôde responder. Revise o modelo, a credencial e a disponibilidade do provedor e tente novamente." });
        return;
      }
      setMessages(current => [...current, { role: "user", content: message }, { role: "assistant", content: data.response, result: data }]);
      setQuestion("");
    });
  };
  const diagnose = (message, index) => {
    const query = messages[index - 1]?.content;
    if (!query) return;
    return request(`/prompt/${prompt?.id}/knowledge/search-preview`, { query, history: boundedHistory(messages.slice(0, index - 1)) }, data => {
      setMessages(current => current.map(item => item === message ? { ...item, searchResult: data } : item));
    });
  };
  const cancel = () => {
    const source = requestRef.current;
    requestRef.current = null;
    source?.cancel("preview cancelled");
    setRunning(false);
    if (source) setNotice({ severity: "info", text: "Teste cancelado. Uma chamada já iniciada pode ter consumido cota." });
  };
  const reset = () => { cancel(); setMessages([]); setQuestion(""); setNotice(null); };
  return { messages, question, setQuestion, running, notice, send, diagnose, cancel, reset };
}
